import { normCell, dayShade } from "./constants";
import SignupRosterPopover from "../signups/SignupRosterPopover";

export default function ScheduleCell({
  entries, staffLookup, studentLookup, color, onClick,
  signups = [], students = [], date, canEdit = true, onSignupChanged, groupLabelByKey = {}, blockColor = "", shadeIndex = 0,
  trips = [], tripDate,
  dayKey, blockKey, onCellDragStart, onClassDragStart, onCellDrop, onItemDragEnd, dragActive = false,
}) {
  const rawClasses = normCell(entries);
  // 1:1 / highlighted classes always render at the bottom of the block (stable otherwise).
  // Keep each class's ORIGINAL index so drag operations target the right entry.
  const classes = rawClasses
    .map((c, i) => ({ c, i }))
    .sort((a, b) => ((a.c.highlight ? 1 : 0) - (b.c.highlight ? 1 : 0)) || (a.i - b.i));
  const isEmpty = classes.length === 0 && signups.length === 0 && trips.length === 0;

  // Slightly different shade of the day's column color per block row (Wednesday grey → subtly varying greys)
  const cellBg = dayShade(color.accent, shadeIndex);

  const getStudentLabel = (entry) => {
    if (entry.student_group_type === "individuals" && entry.student_ids?.length > 0) {
      if (entry.student_ids.length <= 2) return entry.student_ids.map(id => studentLookup?.[id] || "").filter(Boolean).join(", ");
      return `${entry.student_ids.length} students`;
    }
    if (entry.student_group_key && groupLabelByKey[entry.student_group_key]) return groupLabelByKey[entry.student_group_key];
    return entry.student_group || "";
  };

  return (
    <td
      onClick={onClick}
      onDragOver={(e) => { if (canEdit) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
      onDrop={(e) => { if (!canEdit) return; e.preventDefault(); onCellDrop?.(dayKey, blockKey); }}
      style={{ background: cellBg }}
      className={`relative group border px-1.5 py-1 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all min-w-[140px] align-top ${color.border} ${dragActive ? "outline-dashed outline-1 outline-indigo-300" : ""}`}
      data-testid="schedule-cell"
    >
      {canEdit && !isEmpty && (
        <button
          type="button"
          draggable
          onClick={(e) => e.stopPropagation()}
          onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "cell"); onCellDragStart?.(dayKey, blockKey); }}
          onDragEnd={() => onItemDragEnd?.()}
          title="Drag this whole block to another cell"
          data-testid={`cell-drag-handle-${dayKey}-${blockKey}`}
          className="absolute top-0.5 right-0.5 z-10 p-0.5 rounded bg-white/70 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-gray-700 cursor-grab active:cursor-grabbing no-print"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="6" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="9" cy="18" r="1"/><circle cx="15" cy="6" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="18" r="1"/></svg>
        </button>
      )}
      {isEmpty ? (
        <div className="flex items-center justify-center h-full min-h-[50px] text-gray-300 text-xs">+ Add</div>
      ) : (
        <div className="space-y-1 min-h-[50px]">
          {classes.map(({ c: entry, i: origIndex }, i) => {
            const lead = staffLookup[entry?.lead_staff_id] || "";
            const supports = (entry?.support_staff_ids || []).map(id => staffLookup[id]).filter(Boolean);
            const isHighlighted = !!entry.highlight;
            const studentLabel = getStudentLabel(entry);
            // Highlight / 1:1 / pull-out cell with NO student attached — likely the
            // title names a student (e.g. "Erik 1:1") but no roster was linked.
            const unattached = isHighlighted && (entry.student_group_type === "individuals" || /1:1/.test(entry.subject || "")) && (!entry.student_ids || entry.student_ids.length === 0);
            return (
              <div key={i}
                draggable={canEdit}
                onDragStart={canEdit ? (e) => { e.stopPropagation(); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "class"); onClassDragStart?.(dayKey, blockKey, origIndex); } : undefined}
                onDragEnd={canEdit ? () => onItemDragEnd?.() : undefined}
                data-testid={`class-card-${dayKey}-${blockKey}-${origIndex}`}
                className={`${i > 0 ? "mt-1" : ""} rounded-sm pl-1.5 py-0.5 ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
                style={isHighlighted
                  ? { background: "#FEF3C7", borderLeft: unattached ? "3px solid #DC2626" : "3px solid #F59E0B" }
                  : { background: (blockColor || color.accent) + "0D", borderLeft: `3px solid ${blockColor || color.accent}` }}>
                <p className="font-semibold text-xs leading-tight truncate flex items-center gap-1" style={{ color: isHighlighted ? "#92400E" : color.accent }}>
                  {unattached && (
                    <span title="No student attached — this 1:1/pull-out names a student but has no roster linked" data-testid={`unattached-warning-${dayKey}-${blockKey}-${origIndex}`} className="shrink-0 text-rose-600">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                    </span>
                  )}
                  <span className="truncate">{entry.subject}</span>{entry.requires_signup && <span className="ml-1 inline-block align-middle text-[8px] font-bold px-1 py-0 rounded bg-indigo-100 text-indigo-700" title="Optional — students sign up">SIGN-UP</span>}</p>
                {lead && <p className="text-[10px] font-medium text-gray-700 truncate">{lead}</p>}
                {supports.length > 0 && <p className="text-[10px] text-gray-500 truncate">+{supports.join(", ")}</p>}
                {studentLabel && <p className="text-[10px] italic text-gray-500 truncate">{studentLabel}</p>}
                {entry.location && <p className="text-[10px] text-gray-400 truncate">{entry.location}</p>}
              </div>
            );
          })}
          {classes.length > 1 && (
            <div className="text-[9px] text-center font-medium rounded px-1" style={{ color: color.accent, background: color.accent + "15" }}>{classes.length} classes</div>
          )}
          {signups.length > 0 && (
            <div className="mt-1 pt-1 border-t border-dashed border-indigo-300 space-y-0.5" onClick={(e) => e.stopPropagation()} data-testid="cell-signups">
              <p className="text-[8px] font-bold uppercase tracking-wide text-indigo-500">Sign-ups</p>
              {signups.map(({ option, studentCount, studentNames }) => (
                <SignupRosterPopover
                  key={option.id}
                  option={{ ...option, type: "block" }}
                  date={date}
                  studentCount={studentCount}
                  studentNames={studentNames}
                  students={canEdit ? students : []}
                  tone="indigo"
                  readOnly={!canEdit}
                  onChanged={onSignupChanged}
                />
              ))}
            </div>
          )}
          {trips.length > 0 && (
            <div className="mt-1 pt-1 border-t border-dashed border-amber-300 space-y-0.5" onClick={(e) => e.stopPropagation()} data-testid="cell-trips">
              <p className="text-[8px] font-bold uppercase tracking-wide text-amber-600">Trips</p>
              {trips.map((t) => (
                <SignupRosterPopover
                  key={t.id}
                  option={{ id: t.id, title: t.title, lead_staff_name: t.lead_staff_name, location: t.location, capacity: null }}
                  date={null}
                  tripDate={tripDate}
                  supportStaffNames={t.support_staff_names}
                  studentCount={t.yes_count + (t.maybe_count || 0)}
                  studentNames={t.student_names}
                  students={canEdit ? students : []}
                  tone="amber"
                  readOnly={!canEdit}
                  onChanged={onSignupChanged}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </td>
  );
}
